import { XMarkIcon } from '@heroicons/react/24/solid';
import React, { FunctionComponent, ReactNode } from 'react';
import { classNames } from '../../utils/class-names';

type AlertVariant = 'success' | 'danger' | 'warning' | 'purple' | 'secondary';

interface AlertProps {
  variant?: AlertVariant;
  className?: string;
  dismissible?: boolean;
  onClose?: () => void;
  children?: ReactNode;
}

const VARIANT_CLASSES: { [variant in AlertVariant]: string } = {
  success: 'bg-green-50 text-green-800 border-green-200',
  danger: 'bg-red-50 text-red-800 border-red-200',
  warning: 'bg-yellow-50 text-yellow-800 border-yellow-200',
  purple: 'bg-secondary text-white border-transparent rounded-none',
  secondary: 'bg-gray-50 text-gray-800 border-gray-200',
};

/** Tailwind replacement for the react-bootstrap Alert. */
const Alert: FunctionComponent<AlertProps> & {
  Link: FunctionComponent<{
    href?: string;
    as?: React.ElementType;
    children?: ReactNode;
    [key: string]: any;
  }>;
} = ({ variant = 'secondary', className, dismissible, onClose, children }) => (
  <div
    role="alert"
    className={classNames(
      'border rounded-md px-4 py-3 text-sm relative',
      VARIANT_CLASSES[variant],
      className || ''
    )}
  >
    {children}
    {dismissible && (
      <button
        type="button"
        aria-label="Close alert"
        className="absolute top-2 right-2 text-current hover:opacity-70 focus:outline-hidden"
        onClick={() => onClose?.()}
      >
        <XMarkIcon className="h-4 w-4" aria-hidden="true" />
      </button>
    )}
  </div>
);

Alert.Link = ({ as: Component = 'a', children, ...rest }) => (
  <Component {...rest} className="font-bold underline text-current">
    {children}
  </Component>
);

export default Alert;
